import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Tab, TabGroup, TabList } from '@headlessui/react'
import { AiOutlineCloseCircle as CloseIcon } from 'react-icons/ai'
import { MdDragIndicator } from 'react-icons/md'

import { CommonArrayItem } from 'src/common/types'
import Button from 'src/components/Custom/Button/Button'
import CardSection from 'src/components/Custom/Card/CardSection'
import DragAndDropImageArea from 'src/components/Custom/DragAndDropImageArea/DragAndDropImageArea'
import Dropdown from 'src/components/Custom/Dropdown/Dropdown.tsx'
import UpdatableText from 'src/components/Custom/UpdatableText/UpdatableText'
import { nonEmptyStringRule, slugRule } from 'src/lib/validationRules'

import { sourceTabs } from './constants'
import { Livestream } from './Live'

interface LiveItemProps {
  handleImageUpload: (files, item) => void
  isLoading?: boolean
  item: Livestream
  onDelete: () => void
  renderLivestreamSourceFields: (item) => React.JSX.Element
  streamScheduleOptions: CommonArrayItem[]
  setActiveSourcesTab: (value) => void
  updateCurrentChannel: (value: string | null, name, item) => void
  updateLivestream: (id, body) => Promise<void>
}

const LiveItem = ({
  handleImageUpload,
  isLoading,
  item,
  onDelete,
  renderLivestreamSourceFields,
  streamScheduleOptions,
  setActiveSourcesTab,
  updateCurrentChannel,
  updateLivestream,
}: LiveItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.id,
    })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  }
  const classNames = (...classes) => classes.filter(Boolean).join(' ')

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
      }}
    >
      <div
        key={item.id}
        className="rounded-lg border border-gray-200 bg-white p-4"
      >
        <div className="flex w-full items-center justify-center">
          <MdDragIndicator
            size={40}
            className="rotate-90 transform text-gray-500"
            {...listeners}
            {...attributes}
          />
        </div>
        <CardSection title="Name">
          <UpdatableText
            value={item.name}
            onChange={(v) => updateCurrentChannel(v, 'name', item)}
            loadingData={isLoading}
            onSave={() =>
              updateLivestream(item.id, {
                name: item.name,
              })
            }
            validationRules={nonEmptyStringRule()}
          />
        </CardSection>

        <CardSection title="Slug">
          <UpdatableText
            value={item.slug}
            onChange={(v) => updateCurrentChannel(v, 'slug', item)}
            loadingData={isLoading}
            onSave={() =>
              updateLivestream(item.id, {
                slug: item.slug,
              })
            }
            validationRules={slugRule}
          />
        </CardSection>

        <CardSection title="Logo">
          <div className="mb-2 flex flex-col">
            <span className="text-sm">Logo</span>
            <div className="flex flex-col lg:flex-row">
              <div className="mb-2 w-full lg:w-1/2">
                <div className="mt-6">
                  {item.logo ? (
                    <img src={item.logo} alt="primary logo" />
                  ) : (
                    <em>Not set</em>
                  )}
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <DragAndDropImageArea
                  id={`${item.name} logo`}
                  onUpload={(files) => handleImageUpload(files, item)}
                />
              </div>
            </div>
          </div>
        </CardSection>

        <CardSection title="Description">
          <UpdatableText
            value={item.stationDescription}
            onChange={(v) =>
              updateCurrentChannel(v, 'stationDescription', item)
            }
            textArea
            loadingData={isLoading}
            onSave={() =>
              updateLivestream(item.id, {
                description: item.stationDescription,
              })
            }
            validationRules={nonEmptyStringRule()}
          />
        </CardSection>

        <CardSection title="Short description">
          <UpdatableText
            value={item.shortStationDescription}
            onChange={(v) =>
              updateCurrentChannel(v, 'shortStationDescription', item)
            }
            textArea
            loadingData={isLoading}
            onSave={() =>
              updateLivestream(item.id, {
                shortDescription: item.shortStationDescription,
              })
            }
            validationRules={nonEmptyStringRule()}
          />
        </CardSection>

        <CardSection title="Schedule">
          <label
            htmlFor={`${item.id}-stream-schedule`}
            className="block text-sm font-medium text-gray-700"
          >
            Stream schedule
          </label>
          <Dropdown
            buttonText="Select stream schedule"
            fullWidth={true}
            onOptionsChange={async ({ value }) => {
              const streamScheduleId = value || null
              try {
                await updateLivestream(item.id, { streamScheduleId })
              } catch {
                return
              }
            }}
            options={streamScheduleOptions}
            value={item.streamScheduleId ?? ''}
          />
        </CardSection>

        <CardSection
          title="Sources"
          subtitle={
            <div className="space-y-2 text-sm text-gray-500">
              <p>
                Multiple livestream sources can be configured. Any source may be
                used on a given platform.
              </p>
              <div>
                Livestream source order of preference:
                <ol className="list-inside list-decimal">
                  <li>Embed code (web only)</li>
                  <li>Stream URL</li>
                  <li>PBS Station Manager</li>
                  <li>Vimeo</li>
                </ol>
              </div>
            </div>
          }
        >
          <TabGroup
            onChange={(i) =>
              setActiveSourcesTab((prev) => ({ ...prev, [item.id]: i }))
            }
          >
            <TabList className="mt-4 flex space-x-4 rounded-xl bg-white p-1">
              {sourceTabs.map((tab) => (
                <Tab
                  key={tab.id}
                  className={({ selected }) =>
                    classNames(
                      'rounded-lg px-4 py-2.5 text-sm font-medium leading-5',
                      'ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                      selected
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-500 hover:bg-indigo-100/20'
                    )
                  }
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </TabGroup>

          {renderLivestreamSourceFields(item)}
        </CardSection>
        <div className="my-5 flex w-full items-center justify-end">
          <Button
            title={`Delete ${item.name}`}
            outlineColor="border-red-600 text-red-600 hover:border-red-700 hover:text-red-700"
            icon={<CloseIcon size="20px" />}
            onClick={onDelete}
          />
        </div>
      </div>
    </div>
  )
}

export default LiveItem
